import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SyncStateService, SyncStateSnapshot } from './sync-state.service';

/**
 * Cross-tab Cognito-cache sync overlay. Always-mounted at app level.
 * Renders a small fixed banner at the top of the page when a sync is in
 * flight (regardless of which operator triggered it). Non-blocking so
 * admins can keep working on other tabs while the sync runs.
 *
 * Wire by adding `<sync-overlay></sync-overlay>` to app.component.html.
 */
@Component({
  selector: 'sync-overlay',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="state.inProgress"
         class="fixed top-3 left-1/2 -translate-x-1/2 z-[9999]
                bg-[#0f1422] border border-amber-500/40 text-amber-100
                rounded-lg shadow-2xl px-4 py-3 min-w-[420px] max-w-[640px]
                pointer-events-auto">
      <div class="flex items-center gap-3 mb-2">
        <div class="w-3 h-3 rounded-full bg-amber-400 animate-pulse"></div>
        <div class="text-sm font-semibold tracking-wide">
          Refreshing Cognito user cache
        </div>
      </div>
      <div class="text-xs text-amber-200/80 mb-2">
        Started by
        <span class="font-mono">{{ state.byEmail || 'unknown' }}</span>
        <span *ngIf="elapsedLabel" class="ml-1">• {{ elapsedLabel }}</span>
      </div>
      <div class="w-full h-2 rounded-full bg-amber-900/40 overflow-hidden">
        <div class="h-full bg-amber-400 transition-all"
             [style.width.%]="progressPct"></div>
      </div>
      <div class="text-[11px] text-amber-200/60 mt-1 flex justify-between">
        <span>
          {{ state.scannedSoFar | number }}
          <span *ngIf="state.prevTotal > 0">/ ~{{ state.prevTotal | number }}</span>
          users
        </span>
        <span *ngIf="state.prevTotal > 0">{{ progressPct | number:'1.0-0' }}%</span>
      </div>
    </div>
  `,
})
export class SyncOverlayComponent implements OnDestroy {
  state: SyncStateSnapshot = {
    inProgress: false, byEmail: null, startedAt: null,
    prevTotal: 0, scannedSoFar: 0, error: null,
  };
  elapsedLabel = '';
  progressPct = 0;

  private _sub: Subscription;
  private _tick: any;

  constructor(public sync: SyncStateService) {
    this._sub = this.sync.state$.subscribe((s) => {
      this.state = s;
      this._recompute();
    });
    // Tick once a second to keep the elapsed label fresh.
    this._tick = setInterval(() => this._recompute(), 1000);
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
    if (this._tick) clearInterval(this._tick);
  }

  private _recompute() {
    if (!this.state.inProgress) {
      this.elapsedLabel = '';
      this.progressPct = 0;
      return;
    }
    if (this.state.startedAt) {
      const elapsedSec = Math.max(0, (Date.now() - new Date(this.state.startedAt).getTime()) / 1000);
      this.elapsedLabel = elapsedSec < 60
        ? `${Math.round(elapsedSec)}s ago`
        : `${Math.floor(elapsedSec / 60)}m ${Math.round(elapsedSec % 60)}s ago`;
    }
    this.progressPct = this.state.prevTotal > 0
      ? Math.min(100, Math.max(0, (this.state.scannedSoFar / this.state.prevTotal) * 100))
      : 0;
  }
}
